/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { Typography } from 'antd';

import styles from './AnchorNav.less';

const { Text } = Typography;

/** 取不到实测值时的兑底偏移 */
const DEFAULT_ANCHOR_OFFSET = 150;

/** 读实测的吸顶栈高度（应用顶栏 + 页头 + 一级 tab + 二级导航） */
const readContentTop = () => {
  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    !window.getComputedStyle
  ) {
    return DEFAULT_ANCHOR_OFFSET;
  }
  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue('--pg-content-top');
  const value = parseFloat(raw);
  return Number.isFinite(value) ? value : DEFAULT_ANCHOR_OFFSET;
};

/**
 * AnchorNav —— 页内锚点导航（右侧 sticky）
 * ------------------------------------------------------------------
 * 用途：承载「同一实体的多段信息」（商家档案的基础/资质/结算/日志、
 * 成员详情的概览/基础/结算/会话）。这些内容换成 tab 会让用户丢掉
 * 当前实体上下文，因此改用「单页平铺 + 右侧锚点」。
 *
 * 实现要点：
 * 1. **IntersectionObserver 触发 + 全量 rect 兜底计算** —— 只用 IO 回调
 *    里的 entries 会“只拿到变化项”，滚动过快时高亮会跳错；所以 IO 只做
 *    触发器，真正的判定遍历所有区块取「最后一个已越过吸顶线的」，
 *    并用 rAF 合并同一次滚动里的多次触发（避免连续强制布局）。
 *    对外依赖用「区块键串」而不是 items 引用，避免调用方的内联数组
 *    导致观察器反复重建。
 * 1.1 **防抖（120ms）+ 跳转期间抑制** —— 只靠上面的判定会抖：
 *     ① 滚动中图表异步挂载/图片加载会让区块高度变化，吸顶线反复穿越；
 *     ② 点锚点后的平滑滚动会把高亮先切回旧区块、滚过阈值再跳回来。
 *     所以：候选值需连续稳定 120ms 才真正切换；点击跳转后的 700ms 内
 *     不做自动判定（直接把高亮固定在目标区块）。
 * 2. 点击用 smooth 滚动；同时把锚点写进 URL hash（replaceState，
 *    不污染浏览器历史），刷新/分享可还原。
 * 3. `prefers-reduced-motion` 下取消平滑滚动。
 * 4. 每个区块需要自带 scroll-margin-top（见 less），否则会被吸顶导航遮住。
 *
 * @param items   [{ key, title }]，key 必须是页面上区块的 DOM id
 *                （要求全局唯一；重名会让高亮错乱）
 * @param offset  吸顶偏移量（px）；**不传则按实测的吸顶栈高度**（CSS 变量
 *                --pg-content-top，由 ui/stickyLayout.js 发布，兜底 150）
 * @param title   锚点栏小标题，默认「本页内容」
 */
export const AnchorNav = (props) => {
  const { items = [], offset, title = '本页内容', className } = props;

  const [active, setActive] = useState(items[0] ? items[0].key : undefined);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  /* 当前高亮（避免重复 setState 抖动）+ 待确认的防抖定时器 + 点击跳转的待处理状态 */
  const activeRef = useRef(items[0] ? items[0].key : undefined);
  const settleTimerRef = useRef(null);
  const pendingJumpRef = useRef(null);
  /* 依赖用「键串」而不是 items 引用：调用方往往在内联写数组字面量，
     用引用做依赖会让 IntersectionObserver 每次都重建（高亮也会抖）。 */
  const keys = items.map((item) => item.key).join('|');

  useEffect(() => {
    const list = itemsRef.current;
    if (!list.length) return undefined;

    const raf =
      typeof window !== 'undefined' &&
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (cb) => setTimeout(cb, 16);
    const cancel =
      typeof window !== 'undefined' &&
      typeof window.cancelAnimationFrame === 'function'
        ? window.cancelAnimationFrame.bind(window)
        : clearTimeout;

    let ticking = 0;

    /* 候选值需连续稳定一段才真的切换高亮（防抖）。
       滚动中区块高度会因图表挂载/图片加载变化，吸顶线反复穿越时
       直接 setActive 就会看见高亮「来回跳」。
       定时器放 ref 里：点击跳转需要能取消待确认的旧值，否则定时器
       会在跳转后回写旧高亮（竞态）。 */
    const commit = (key) => {
      if (key === activeRef.current) return;
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      settleTimerRef.current = setTimeout(() => {
        settleTimerRef.current = null;
        /* 期间若发生了点击跳转，就丢弃这个旧候选值 */
        if (pendingJumpRef.current) return;
        activeRef.current = key;
        setActive(key);
      }, 120);
    };

    /* 重算用 rAF 合并：IO 可能在一次滚动里连续触发多次，
       逐次遍历取 rect 会造成不必要的强制布局。
       偏移每次重算时现读 CSS 变量：首帧该变量可能还没发布。 */
    const recompute = () => {
      if (ticking) return;
      ticking = raf(() => {
        ticking = 0;
        const top = offset === undefined ? readContentTop() : offset;

        /* 点击跳转后：高亮已固定在目标区块，等它**真正滚到位**再恢复自动判定。
           用固定时间窗不可靠（滚动距离长时平滑动画可能超过窗口），
           所以按“目标区块是否已到达吸顶线”来判断，并留 1.5s 上限兜底。 */
        const pending = pendingJumpRef.current;
        if (pending) {
          const target = document.getElementById(pending.key);
          const arrived =
            target && Math.abs(target.getBoundingClientRect().top - top) <= 8;
          if (!arrived && Date.now() < pending.until) return;
          pendingJumpRef.current = null;
        }

        let current = list[0].key;
        list.forEach((item) => {
          const el = document.getElementById(item.key);
          if (el && el.getBoundingClientRect().top <= top + 4)
            current = item.key;
        });
        commit(current);
      });
    };

    /* 区块列表变化后，旧高亮可能已不存在 → 回到第一块 */
    if (!list.some((item) => item.key === activeRef.current)) {
      activeRef.current = list[0].key;
      setActive(list[0].key);
    }

    const io = new IntersectionObserver(recompute, {
      rootMargin: `-${
        offset === undefined ? readContentTop() : offset
      }px 0px -55% 0px`,
      threshold: [0, 0.25, 0.5, 1],
    });

    list.forEach((item) => {
      const el = document.getElementById(item.key);
      if (el) io.observe(el);
    });

    return () => {
      io.disconnect();
      if (ticking) cancel(ticking);
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    };
  }, [keys, offset]);

  const handleJump = (event, key) => {
    event.preventDefault();
    const el = document.getElementById(key);
    if (!el) return;
    /* 立刻高亮目标区块，并登记“跳转中”：自动判定会等它真正滚到位
       （或 1.5s 兜底超时）才恢复，避免平滑滚动把高亮抢回旧区块。
       同时取消待确认的防抖定时器，防止它回写旧值。 */
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    pendingJumpRef.current = { key, until: Date.now() + 1500 };
    activeRef.current = key;
    setActive(key);
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    setActive(key);
    if (
      typeof window !== 'undefined' &&
      window.history &&
      window.history.replaceState
    ) {
      window.history.replaceState(null, '', `#${key}`);
    }
  };

  if (!items.length) return null;

  return (
    <nav className={`${styles.anchor} ${className || ''}`} aria-label={title}>
      <Text className={styles.title} type="secondary">
        {title}
      </Text>
      {items.map((item) => (
        <a
          key={item.key}
          href={`#${item.key}`}
          className={styles.link}
          aria-current={active === item.key ? 'true' : undefined}
          onClick={(e) => handleJump(e, item.key)}
        >
          {item.title}
        </a>
      ))}
    </nav>
  );
};

/**
 * AnchorLayout —— 「左内容 + 右锚点」两栏骨架
 * 统一栅格与间距，页面不用各写 grid-template-columns。
 *
 * @param items  透传给 AnchorNav
 * @param offset 透传给 AnchorNav
 */
export const AnchorLayout = (props) => {
  const { items = [], offset, title, children, className } = props;
  return (
    <div className={`${styles.layout} ${className || ''}`}>
      <div className={styles.main}>{children}</div>
      <AnchorNav items={items} offset={offset} title={title} />
    </div>
  );
};

export default AnchorNav;
